/**
 * Coming Soon Service
 * 
 * Frontend service for Coming Soon poster generation and refinement.
 * Handles API communication with the backend Gemini image service.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Get all available visual styles
 * @returns {Promise<{success: boolean, styles: Array}>}
 */
export const getVisualStyles = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/ai/coming-soon/styles`);
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to fetch visual styles:', error);
        return {
            success: false,
            error: error.message,
            styles: []
        };
    }
};

/**
 * Get quick actions for poster refinement
 * @returns {Promise<{success: boolean, quickActions: Array}>}
 */
export const getQuickActions = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/ai/coming-soon/quick-actions`);
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to fetch quick actions:', error);
        return {
            success: false,
            error: error.message,
            quickActions: []
        };
    }
};

/**
 * Generate a Coming Soon poster
 * 
 * @param {Object} params - Generation parameters
 * @param {string} params.styleId - Visual style ID
 * @param {string} params.guestImage - Base64 encoded guest image
 * @param {Object} params.context - Content context
 * @param {string} params.context.guestName - Guest name
 * @param {string} params.context.occupation - Guest occupation
 * @param {string} params.context.guestBio - Short bio
 * @param {string} params.context.topic - Discussion topic
 * @param {Object} params.options - Additional options
 * @param {string} params.tenantId - Tenant ID
 * @returns {Promise<Object>} Generation result
 */
export const generatePoster = async ({
    styleId,
    guestImage,
    context,
    options = {},
    tenantId = 'default'
}) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/ai/coming-soon/generate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                styleId,
                guestImage,
                context,
                options,
                tenantId
            })
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to generate poster:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Refine an existing poster
 * 
 * @param {Object} params - Refinement parameters
 * @param {string} params.currentPosterUrl - URL of current poster
 * @param {string} params.instruction - User's edit instruction
 * @param {Array} params.conversationHistory - Previous messages
 * @param {string} params.tenantId - Tenant ID
 * @returns {Promise<Object>} Refinement result
 */
export const refinePoster = async ({
    currentPosterUrl,
    instruction,
    conversationHistory = [],
    tenantId = 'default'
}) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/ai/coming-soon/refine`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                currentPosterUrl,
                instruction,
                conversationHistory,
                tenantId
            })
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to refine poster:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Parse a user instruction to understand the edit intent
 * 
 * @param {string} instruction - User's instruction
 * @returns {Promise<Object>} Parsed instruction result
 */
export const parseInstruction = async (instruction) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/ai/coming-soon/parse-instruction`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ instruction })
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to parse instruction:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Chat with the AI co-pilot for Coming Soon poster
 * 
 * @param {Object} params - Chat parameters
 * @param {string} params.message - User's message
 * @param {Array} params.conversationHistory - Previous messages
 * @param {Object} params.posterContext - Current poster context
 * @param {string} params.guestImage - Base64 guest image (for regeneration)
 * @param {string} params.tenantId - Tenant ID
 * @returns {Promise<Object>} Chat result with response and optional poster updates
 */
export const chat = async ({
    message,
    conversationHistory = [],
    posterContext = {},
    guestImage,
    tenantId = 'default'
}) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/ai/coming-soon/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                message,
                conversationHistory,
                posterContext,
                guestImage,
                tenantId
            })
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to chat with co-pilot:', error);
        return {
            success: false,
            error: error.message,
            response: "I encountered an error. Please try again."
        };
    }
};

/**
 * Convert a File to base64 string
 * 
 * @param {File} file - Image file
 * @returns {Promise<string>} Base64 encoded image
 */
export const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
};

/**
 * Validate an image file
 * 
 * @param {File} file - Image file to validate
 * @returns {Object} Validation result
 */
export const validateImageFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (!file) {
        return { valid: false, error: 'No file provided' };
    }

    if (!allowedTypes.includes(file.type)) {
        return {
            valid: false,
            error: `Invalid file type. Allowed: ${allowedTypes.join(', ')}`
        };
    }

    if (file.size > maxSize) {
        return {
            valid: false,
            error: `File too large. Maximum size: ${maxSize / (1024 * 1024)}MB`
        };
    }

    return { valid: true };
};

// Export as default object
const comingSoonService = {
    getVisualStyles,
    getQuickActions,
    generatePoster,
    refinePoster,
    parseInstruction,
    chat,
    fileToBase64,
    validateImageFile
};

export default comingSoonService;